import { useEffect, useRef, useState, type ReactNode } from "react";

import { ThemeScope } from "../theme/ThemeScope.js";
import { SHIPPED_SYSTEM, type ThemeMode, type ThemeSystem } from "../theme/themes.js";
import type { VisitorLogo } from "../logos.js";
import { WheelDial } from "./dial.js";
import { Cta, FootFab, SiteFoot, WheelLogo } from "./parts.js";
import { DEFAULT_WHEEL_COPY, type WheelCopy, type WheelLane, type WheelSegment } from "./types.js";
import "../styles/wheel.css";

export type SpinPick =
  | number
  | { readonly index: number; readonly claim?: string; readonly label?: string };

export type WheelResult = {
  readonly segment: WheelSegment | null;
  readonly claim: string;
  readonly email: string;
};

export const WHEEL_SPIN_MS = 4900;

export type WheelProps = {
  readonly lane?: WheelLane;
  readonly system?: ThemeSystem;
  readonly mode?: ThemeMode;
  readonly segments: readonly WheelSegment[];
  readonly copy?: WheelCopy;
  readonly logo?: VisitorLogo;
  readonly showLabels?: boolean;
  readonly spinning?: boolean;
  readonly landed?: number | null;
  readonly resolveSpin?: () => Promise<SpinPick> | SpinPick;
  readonly onSpin?: () => void;
  readonly onResult?: (result: WheelResult) => void;
  readonly copyColumn?: ReactNode;
  readonly showFooter?: boolean;
  readonly footerStartsOpen?: boolean;
  /** When false the dial is not flickable. Registration stages pass false. */
  readonly interactive?: boolean;
};

/**
 * The prize dial. The service picks the segment; this component only animates
 * to the index it is handed. Portrait and landscape follow the box it is mounted in.
 */
export function Wheel({
  lane = "onsite",
  system = SHIPPED_SYSTEM,
  mode = "light",
  segments,
  copy = DEFAULT_WHEEL_COPY,
  logo = { src: null, name: "Nova" },
  showLabels = false,
  spinning: spinningProp,
  landed: landedProp,
  resolveSpin,
  onSpin,
  onResult,
  copyColumn,
  showFooter = true,
  footerStartsOpen = lane === "web",
  interactive = true,
}: WheelProps): ReactNode {
  const [orient, setOrient] = useState<"landscape" | "portrait">("landscape");
  const [footOpen, setFootOpen] = useState(footerStartsOpen);
  const [spinningLocal, setSpinningLocal] = useState(false);
  const [landedLocal, setLandedLocal] = useState<number | null>(null);
  const surface = useRef<HTMLDivElement | null>(null);
  const flow = useRef<HTMLDivElement | null>(null);

  const spinning = spinningProp ?? spinningLocal;
  const landed = landedProp ?? landedLocal;

  useEffect(() => {
    const el = surface.current;
    if (el === null || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      if (entry === undefined) return;
      const { width, height } = entry.contentRect;
      setOrient(height > width ? "portrait" : "landscape");
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const spin = async (): Promise<void> => {
    if (spinning) return;
    if (onSpin !== undefined) {
      onSpin();
      return;
    }
    try {
      const pick = resolveSpin === undefined ? Math.floor(Math.random() * segments.length) : await resolveSpin();
      const index = typeof pick === "number" ? pick : pick.index;
      const claim = typeof pick === "number" ? "" : (pick.claim ?? "");
      setLandedLocal(index);
      setSpinningLocal(true);
      window.setTimeout(() => {
        setSpinningLocal(false);
        const segment = segments[index] ?? null;
        if (onResult !== undefined) onResult({ segment, claim, email: "" });
      }, WHEEL_SPIN_MS);
    } catch {
      setSpinningLocal(false);
    }
  };

  const toggleFoot = (): void => {
    if (lane === "onsite") {
      setFootOpen((open) => !open);
      return;
    }
    const el = flow.current;
    if (el === null) return;
    const atTop = el.scrollTop < 40;
    el.scrollTo({ top: atTop ? el.scrollHeight : 0, behavior: "smooth" });
    setFootOpen(atTop);
  };

  const column =
    copyColumn ??
    (
      <div className="col">
        <WheelLogo logo={logo} />
        <h1 className="head">{copy.heading}</h1>
        <p className="body">{copy.body}</p>
        <Cta ready={!spinning} disabled={spinning} onClick={() => void spin()}>
          {spinning ? "Spinning…" : copy.cta}
        </Cta>
      </div>
    );

  return (
    <div className="host" data-lane={lane}>
      <div className="fitter">
        <ThemeScope
          ref={surface}
          surface="wheel"
          system={system}
          mode={mode}
          className="surface"
          data-wheel-mode={mode}
        >
          <div className="flow" ref={flow}>
            <div className="play" data-orient={orient}>
              {column}
              <WheelDial
                segments={segments}
                spinning={spinning}
                landed={landed}
                showLabels={showLabels}
                onFlick={interactive && !spinning ? () => void spin() : null}
              />
            </div>
            {showFooter && lane === "web" ? <SiteFoot system={system} /> : null}
          </div>
          {showFooter && lane === "onsite" ? (
            <div className="foot-panel" data-open={footOpen ? "true" : "false"}>
              <SiteFoot system={system} />
            </div>
          ) : null}
          {showFooter ? <FootFab open={footOpen} onClick={toggleFoot} /> : null}
        </ThemeScope>
      </div>
    </div>
  );
}
